import './SoftwareTestReport.css';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, RefreshCw, ExternalLink, FileSearch } from 'lucide-react';
import { useStrReport } from '../../hooks/useStrReport';
import { getProjectReadiness, getLevelReason } from './projectReadiness';
import EpicPicker from './EpicPicker';
import StpGeneration from './StpGeneration';
import TestCaseGeneration from './TestCaseGeneration';

const COLORS = {
  success: '#16a34a',
  error: '#dc2626',
  warning: '#f59e0b',
  info: '#3b82f6',
  critical: '#ea580c',
  neutral: '#d1d5db'
};

function SoftwareTestReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const epicKey = searchParams.get('epic') || '';
  // The key is kept in the URL so reports stay shareable and the STR agent's dashboard link keeps working
  const selectEpic = (key) => setSearchParams({ epic: key });
  const { epicData, scoreData, scoreError, loading, error, retry } = useStrReport(epicKey);

  const getLevelColor = (level) => {
    switch (level) {
      case 'GREEN': return COLORS.success;
      // Darker than COLORS.warning so the white badge text stays readable (about 5:1 contrast)
      case 'AMBER': return '#b45309';
      case 'RED': return COLORS.error;
      default: return '#6b7280';
    }
  };

  const sanitizeUrl = (url) => {
    const SAFE_URL_PATTERN = /^https?:\/\/[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]+$/;
    if (typeof url !== 'string' || !SAFE_URL_PATTERN.test(url)) return '#';
    return url;
  };

  if (!epicKey) {
    return (
      <div className="str-container">
        <div className="str-empty">
          <FileSearch size={40} />
          <h2>Software Test Report</h2>
          <p>Enter a Jira epic key to load its test report.</p>
          <EpicPicker onSubmit={selectEpic} />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="str-container">
        <div className="str-loading">
          <RefreshCw size={32} className="spinner" />
          <p>Loading test report for {epicKey}...</p>
        </div>
      </div>
    );
  }

  if (error && epicKey) {
    return (
      <div className="str-container">
        <div className="str-error">
          <AlertCircle size={32} />
          <h3>Unable to Load Report</h3>
          <p>{error}</p>
          <EpicPicker initialValue={epicKey} onSubmit={selectEpic} compact />
          <button onClick={retry} className="str-retry-btn">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!epicData) {
    return (
      <div className="str-container">
        <div className="str-empty">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const bugsByStatus = epicData.bugsBySeverity || {};
  const totalBugs = Object.values(bugsByStatus).reduce((sum, severity) => sum + (severity.open || 0) + (severity.resolved || 0), 0);
  const openCriticalBugs = bugsByStatus['P0']?.open || 0;
  const openHighBugs = bugsByStatus['P1']?.open || 0;
  const openBugs = (priority) => bugsByStatus[priority]?.open || 0;
  const resolvedBugs = Object.values(bugsByStatus).reduce((sum, severity) => sum + (severity.resolved || 0), 0);
  const totalTests = epicData.testCases?.length || 0;
  const passedTests = epicData.testCases?.filter(t => t.status === 'passed').length || 0;
  const failedTests = epicData.testCases?.filter(t => t.status === 'failed').length || 0;
  const blockedTests = epicData.testCases?.filter(t => t.status === 'blocked').length || 0;
  const unexecutedTests = epicData.testCases?.filter(t =>
    !t.status || t.status === 'unexecuted'
  ).length || 0;
  const automatedTests = epicData.testCases?.filter(t =>
    t.automationStatus && t.automationStatus.startsWith('Automated')
  ).length || 0;
  const automationCoverage = totalTests > 0 ? Math.round((automatedTests / totalTests) * 100) : 0;
  const pct = (count) => (totalTests > 0 ? (count / totalTests) * 100 : 0);
  const readiness = getProjectReadiness(scoreData, { openCriticalBugs, openHighBugs, automationCoverage }, scoreError, epicData.liveScore);
  const levelColor = getLevelColor(readiness.level);
  const { insight } = readiness;
  const hasInsight = Boolean(insight.headline || insight.summary);

  return (
    <div className="str-page">
      {/* Header */}
      <div className="str-header">
        <div className="str-header-content">
          <div className="str-header-title">
            <p className="str-epic-summary">{epicKey}</p>
            <h1 title={epicData.epicSummary}>{epicData.epicSummary}</h1>
          </div>
          <div className="str-header-meta">
            {epicData.epicOwner && <span className="str-meta-item">{epicData.epicOwner}</span>}
            {epicData.epicStatus && <span className="str-meta-item">{epicData.epicStatus}</span>}
            <span className="str-status-flag" style={{ backgroundColor: levelColor }}>
              {readiness.level}
            </span>
          </div>
        </div>
        <button onClick={retry} className="str-refresh-btn" aria-label="Refresh">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Main Content */}
      <div className="str-main">
        {/* Reference Links */}
        <div className="str-links-section str-links-toolbar">
          <EpicPicker initialValue={epicKey} onSubmit={selectEpic} compact />
          {epicData.jiraEpicUrl && (
            <a href={sanitizeUrl(epicData.jiraEpicUrl)} target="_blank" rel="noopener noreferrer" className="str-link">
              <ExternalLink size={16} /> View Epic in Jira
            </a>
          )}
          {epicData.stpLink?.url ? (
            <a href={sanitizeUrl(epicData.stpLink.url)} target="_blank" rel="noopener noreferrer" className="str-link" title={epicData.stpLink.title}>
              <ExternalLink size={16} /> View STP
            </a>
          ) : epicData.stpLookupFailed ? (
            // "No STP" here only means Jira or Confluence couldn't be read, so don't offer to make one
            <p className="str-stp-status">Couldn't check for an STP. Refresh to try again.</p>
          ) : (
            // The status shows to everyone; the Generate and Reset buttons inside are role-gated
            <StpGeneration key={epicKey} epicKey={epicKey} request={epicData.stpRequest} />
          )}
        </div>

        {/* Feature Flags */}
        {epicData.featureFlags && epicData.featureFlags.length > 0 && (
          <div className="str-links-section">
            <h4>Feature Flags</h4>
            <div className="str-feature-flags">
              {epicData.featureFlags.map((flag) => (
                <span key={flag.key} className="str-flag-chip">
                  <span className="str-flag-name">{flag.name || flag.key}</span>
                  {flag.status && <span className="str-flag-status">{flag.status}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Project Readiness Score */}
        <div className="str-readiness-layout">
        <div className={`str-readiness${readiness.isSample ? ' str-readiness-sample' : ''}`} style={{ borderLeftColor: levelColor }}>
          <div className="str-readiness-summary">
            <div className="str-card-label">Project Readiness Score</div>
            <div className="str-readiness-value">
              <span className="str-readiness-score">{readiness.score ?? '–'}</span>
              <span className="str-readiness-max">/100</span>
              <span className="str-readiness-level" style={{ backgroundColor: levelColor }}>{readiness.level}</span>
            </div>
            {readiness.isUnavailable ? (
              <p className="str-readiness-note str-readiness-error">
                Couldn't load the readiness score ({readiness.error}). Refresh to try again.
              </p>
            ) : readiness.isSample ? (
              <p className="str-readiness-note">
                Sample score for illustration only. No readiness score could be calculated for this epic.
              </p>
            ) : readiness.isLive ? (
              <p className="str-readiness-note">
                Calculated live from Jira and test data.
              </p>
            ) : (
              readiness.generatedAt && (
                <p className="str-readiness-note">Generated {new Date(readiness.generatedAt).toLocaleString()}</p>
              )
            )}
            <p className="str-readiness-rule">
              Green needs no open P0/P1 bugs and more than 50% automation.
            </p>
          </div>
          <div className="str-readiness-components">
            {readiness.components.map((component) => (
              <div key={component.key} className="str-readiness-component">
                <div className="str-readiness-component-head">
                  <span>{component.label}</span>
                  <span className="str-readiness-weight">{component.weight}%</span>
                </div>
                <div className="str-readiness-track">
                  <div className="str-readiness-fill" style={{ width: `${component.score ?? 0}%` }}></div>
                </div>
                <span className="str-readiness-component-score">{component.score ?? '–'}</span>
              </div>
            ))}
          </div>
          {hasInsight && (
            <section className="str-ai-summary" aria-label="AI summary">
              <div className="str-ai-summary-head">
                <h3>AI summary</h3>
                <span className="str-ai-badge">{readiness.isSample ? 'Sample' : 'AI generated'}</span>
              </div>
              {insight.headline && <p className="str-ai-headline">{insight.headline}</p>}
              {insight.summary && <p className="str-ai-text">{insight.summary}</p>}
              {readiness.isLive && readiness.generatedAt && (
                // The text was written for the score at that time; the score above is current
                <p className="str-ai-generated">Written {new Date(readiness.generatedAt).toLocaleString()}, for the data at that time.</p>
              )}
            </section>
          )}
          {!hasInsight && readiness.insightError && (
            <p className="str-readiness-note str-readiness-error str-ai-unavailable">
              Couldn't load the AI summary ({readiness.insightError}). Refresh to try again.
            </p>
          )}
        </div>

        {!readiness.isUnavailable && (
          <aside className="str-level-card" aria-label="Readiness level">
            <h3 className="str-level-card-title" style={{ borderBottomColor: levelColor }}>Readiness level</h3>
            <ul className="str-level-legend">
              <li><span className="str-level-dot" style={{ backgroundColor: getLevelColor('GREEN') }} /><strong>Green</strong> No open P0/P1 bugs and &gt;50% automation</li>
              <li><span className="str-level-dot" style={{ backgroundColor: getLevelColor('RED') }} /><strong>Red</strong> Any open P0/P1 bug</li>
              <li><span className="str-level-dot" style={{ backgroundColor: getLevelColor('AMBER') }} /><strong>Amber</strong> Everything else</li>
            </ul>
            {insight.nextSteps.length > 0 && (
              <>
                <h4 className="str-level-subtitle">Recommended next steps</h4>
                <ol className="str-next-steps">
                  {insight.nextSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </>
            )}
            <div className="str-why">
              <h4 className="str-level-subtitle">Why {readiness.level}?</h4>
              <p>{getLevelReason(readiness.level, { openCriticalBugs, openHighBugs, automationCoverage })}</p>
            </div>
          </aside>
        )}
        </div>

        {/* Metric Cards */}
        <div className="str-metrics">
          {/* Bugs Card */}
          <div className="str-card">
            <div className="str-card-label">Bugs Reported</div>
            <div className="str-card-value">{totalBugs}</div>
            {/* Open counts per priority plus resolved add up to the total above */}
            <div className="str-card-breakdown">
              <span style={{ color: COLORS.error }}>P0 Blocker: {openBugs('P0')} open</span>
              <span style={{ color: COLORS.critical }}>P1 High: {openBugs('P1')} open</span>
              <span style={{ color: COLORS.warning }}>P2 Medium: {openBugs('P2')} open</span>
              <span>P3 Low: {openBugs('P3')} open</span>
              <span style={{ color: COLORS.success }}>Resolved: {resolvedBugs}</span>
            </div>
          </div>

          {/* Tests Card */}
          <div className="str-card">
            <div className="str-card-label">Tests Created</div>
            <div className="str-card-value">{totalTests}</div>
            {totalTests > 0 ? (
              <div className="str-card-breakdown">
                <span style={{ color: COLORS.success }}>Passed: {passedTests}</span>
                <span style={{ color: COLORS.error }}>Failed: {failedTests}</span>
                <span style={{ color: COLORS.warning }}>Blocked: {blockedTests}</span>
              </div>
            ) : (
              <>
                <p className="str-card-empty">Oops, looks like no test cases exist for this epic yet.</p>
                {/* QA Compass can write the epic's test cases; only write and admin users get the button */}
                <TestCaseGeneration key={epicKey} epicKey={epicKey} />
              </>
            )}
          </div>

          {/* Coverage Card */}
          <div className="str-card">
            <div className="str-card-label">Test Execution</div>
            <div className="str-card-value">{totalTests - unexecutedTests}/{totalTests}</div>
            <div className="str-card-breakdown">
              <span>Run: {totalTests - unexecutedTests}</span>
              <span style={{ color: COLORS.neutral }}>Not run: {unexecutedTests}</span>
            </div>
          </div>

          {/* Automation Card */}
          <div className="str-card">
            <div className="str-card-label">Automation Coverage</div>
            <div className="str-card-value">{automationCoverage}%</div>
            <div className="str-card-breakdown">
              <span style={{ color: COLORS.info }}>Automated: {automatedTests}</span>
              <span style={{ color: COLORS.neutral }}>Manual: {totalTests - automatedTests}</span>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="str-charts">
          {/* Execution Status Chart */}
          <div className="str-chart">
            <h4>Test Execution Status</h4>
            <div className="str-bar-chart">
              <div className="str-bar-segment" style={{ width: `${pct(passedTests)}%`, backgroundColor: COLORS.success }} title={`Passed: ${passedTests}`}></div>
              <div className="str-bar-segment" style={{ width: `${pct(failedTests)}%`, backgroundColor: COLORS.error }} title={`Failed: ${failedTests}`}></div>
              <div className="str-bar-segment" style={{ width: `${pct(blockedTests)}%`, backgroundColor: COLORS.warning }} title={`Blocked: ${blockedTests}`}></div>
              <div className="str-bar-segment" style={{ width: `${pct(unexecutedTests)}%`, backgroundColor: COLORS.neutral }} title={`Not run: ${unexecutedTests}`}></div>
            </div>
            <div className="str-chart-legend">
              <span><span className="str-legend-dot" style={{ backgroundColor: COLORS.success }}></span> Passed ({passedTests})</span>
              <span><span className="str-legend-dot" style={{ backgroundColor: COLORS.error }}></span> Failed ({failedTests})</span>
              <span><span className="str-legend-dot" style={{ backgroundColor: COLORS.warning }}></span> Blocked ({blockedTests})</span>
              <span><span className="str-legend-dot" style={{ backgroundColor: COLORS.neutral }}></span> Not run ({unexecutedTests})</span>
            </div>
          </div>

          {/* Automation Mix Chart */}
          <div className="str-chart">
            <h4>Automation Mix</h4>
            <div className="str-donut-chart">
              <div className="str-donut" style={{
                background: `conic-gradient(${COLORS.info} 0deg ${automationCoverage*3.6}deg, ${COLORS.neutral} ${automationCoverage*3.6}deg 360deg)`
              }}></div>
              <div className="str-donut-label">
                <div className="str-donut-percent">{automationCoverage}%</div>
                <div className="str-donut-text">Automated</div>
              </div>
            </div>
            <div className="str-chart-legend">
              <span><span className="str-legend-dot" style={{ backgroundColor: COLORS.info }}></span> Automated ({automatedTests})</span>
              <span><span className="str-legend-dot" style={{ backgroundColor: COLORS.neutral }}></span> Manual ({totalTests - automatedTests})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Risk Line */}
      <div className="str-footer">
        <div className="str-risk-item">
          {/* Same bugs that make the readiness level RED */}
          <span className="str-risk-label">Open P0/P1 Bugs:</span>
          <span className="str-risk-value" style={{ color: openCriticalBugs + openHighBugs > 0 ? COLORS.error : COLORS.success }}>{openCriticalBugs + openHighBugs}</span>
        </div>
        <div className="str-risk-item">
          <span className="str-risk-label">Tests Not Run:</span>
          <span className="str-risk-value" style={{ color: unexecutedTests > 0 ? COLORS.warning : COLORS.success }}>{unexecutedTests}</span>
        </div>
        <div className="str-risk-item">
          <span className="str-risk-label">Automation Gap:</span>
          <span className="str-risk-value" style={{ color: automationCoverage < 70 ? COLORS.warning : COLORS.success }}>{totalTests - automatedTests}</span>
        </div>
      </div>
    </div>
  );
}

export default SoftwareTestReport;
